import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { AiOutlineMenu } from "react-icons/ai";

import { resetCar } from "../../redux/car";
import { selectMechanicId } from "../../redux/mechanic";

import {
  getAllSkuBrandsGivenSkuCategoryId,
  getSkuSubCategories,
  getAllSkus,
} from "../../services/sku";

import { SKU_SEARCH_PARAMS } from "../../constants/sku";

import Header from "../../components/Categories/Header/Header";
import ProductsFilter from "../../components/ProductListing/ProductsFilter/ProductsFilter";
import ProductsGrid from "../../components/ProductListing/ProductsGrid/ProductsGrid";
import OverLay from "../../components/ui/OverLay/OverLay";
import CarDropdowns from "../../components/CarDropdowns/CarDropdowns";
import MechanicAutoComplete from "../../components/MechanicAutoComplete/MechanicAutoComplete";
import PromoBanner from "../../components/PromoBanner/PromoBanner";

const ProductListing = () => {
  const { selectedCar, allCarBrands } = useSelector((state) => state.car);
  const selectedMechanicId = useSelector(selectMechanicId);
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryName = searchParams.get("categoryName");
  const categoryId = searchParams.get("categoryId");
  const subCategoryId = searchParams.get("subCategoryId");
  const skuBrandIdList = searchParams.get("skuBrandIdList");
  const skuNameIdList = searchParams.get("skuNameIdList");
  const searchName = searchParams.get(SKU_SEARCH_PARAMS.SKU_COMMERCIAL_NAME);
  const skuIdSearchParam = searchParams.get(SKU_SEARCH_PARAMS.SKU_ID);
  const hasOfficialImage = searchParams.get(
    SKU_SEARCH_PARAMS.IS_OFFICIAL_IMAGE
  );

  const navigate = useNavigate();

  const [filterParams, setFilterParams] = useState({
    skuCommercialName: searchName,
    skuId: skuIdSearchParam,
    hasOfficialImage: hasOfficialImage,
  });
  const mainSectionRef = useRef();

  const filterSkus = useCallback(
    (param) => {
      Object.keys(param).forEach((key) => {
        searchParams.set(key, param[key]);
      });

      setFilterParams((prevParams) => ({ ...prevParams, ...param }));
      setSearchParams(searchParams);
      setCurrentPage(1);
    },
    [searchParams, setSearchParams]
  );

  const resetFiltersHandler = () => {
    setSearchParams({});
    setFilterParams({});
    dispatch(resetCar());
    navigate(`/skus`);
  };

  const [currentPage, setCurrentPage] = useState(1);

  const pageChangedHandler = (newPage) => {
    setCurrentPage(newPage);
  };

  const [limit] = useState(24);

  const [skusList, setSkusList] = useState([]);
  const [skusTotalCount, setSkusTotalCount] = useState();

  const [skusLoading, setSkusLoading] = useState(false);

  useEffect(() => {
    const carParams = { ...filterParams };
    if (selectedCar.brand) {
      carParams.carBrandId = selectedCar.brand.id;
      delete carParams["carModelId"];
      delete carParams["carStartYear"];
      delete carParams["carEndYear"];
      delete carParams["carId"];
    }
    if (selectedCar.model) {
      carParams.carModelId = selectedCar.model.id;
      delete carParams["carStartYear"];
      delete carParams["carEndYear"];
      delete carParams["carId"];
    }
    if (selectedCar.year) {
      carParams.carStartYear = selectedCar.year.startYear;
      carParams.carEndYear = selectedCar.year.endYear;
      delete carParams["carId"];
    }
    if (selectedCar.carDetails) {
      carParams.carId = selectedCar.carDetails.id;
    }
    const getSkus = async () => {
      setSkusLoading(true);
      const params = {
        categoryId,
        subCategoryId,
        skuBrandIdList,
        skuNameIdList,
        skuCommercialName: searchName,
        limit: limit,
        offset: currentPage - 1,
        mechanicId: selectedMechanicId,
        ...carParams,
      };
      try {
        const response = await getAllSkus(params);
        setSkusList(response.data.skuListDetails.skuList);
        setSkusTotalCount(response.data.skuListDetails.skusCount);
      } catch (error) {
        console.log(error);
      }
      setSkusLoading(false);
    };

    getSkus();
  }, [
    categoryId,
    subCategoryId,
    skuBrandIdList,
    skuNameIdList,
    limit,
    currentPage,
    filterParams,
    selectedCar,
    searchName,
    selectedMechanicId,
  ]);

  const [subCategories, setSubCategories] = useState([]);
  const [subCategoriesLoading, setSubCategoriesLoading] = useState(false);

  useEffect(() => {
    if (categoryId && !isNaN(categoryId)) {
      const getSubCategories = async () => {
        setSubCategoriesLoading(true);
        try {
          const response = await getSkuSubCategories(categoryId);
          setSubCategories(response.data.data);
        } catch (error) {
          console.log(error);
        }

        setSubCategoriesLoading(false);
      };

      getSubCategories();
    }
  }, [categoryId]);

  const [brands, setBrands] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(false);

  useEffect(() => {
    const getBrands = async () => {
      setBrandsLoading(true);
      try {
        const response = await getAllSkuBrandsGivenSkuCategoryId(categoryId);
        setBrands(response.data.skuBrandList);
      } catch (error) {
        console.log(error);
      }
      setBrandsLoading(false);
    };

    getBrands();
  }, [categoryId]);

  const [carListLoading, setCarListLoading] = useState(false);
  const [carList, setCarList] = useState([]);

  useEffect(() => {
    if (allCarBrands.length === 0) {
      setCarListLoading(true);
    } else {
      setCarList(allCarBrands);
      setCarListLoading(false);
    }
  }, [allCarBrands]);

  const [filtersShown, setFiltersShown] = useState(false);

  const showProductsFilters = () => {
    setFiltersShown(true);
  };

  const hideProductsFilter = () => {
    setFiltersShown(false);
  };

  useEffect(() => {
    mainSectionRef.current.scrollIntoView({ behavior: "smooth" });
  }, [filterParams, currentPage]);

  return (
    <div className="flex flex-col min-h-screen">
      <Header filterSkus={filterSkus} />
      <main dir="rtl" ref={mainSectionRef}>
        <CarDropdowns />
        <MechanicAutoComplete />
        <PromoBanner />
        <div className="mt-16 mb-32 pl-4 pr-1 sm:pl-16 sm:pr-16 flex gap-4 sm:gap-8 max-w-full">
          <AiOutlineMenu
            size={25}
            className="cursor-pointer block lg:hidden shrink-0"
            onClick={showProductsFilters}
          />
          {filtersShown && (
            <OverLay clicked={hideProductsFilter} breakPoint={1024} />
          )}
          <ProductsFilter
            carList={carList}
            carListLoading={carListLoading}
            brands={brands}
            brandsLoading={brandsLoading}
            filterParams={filterParams}
            filterSkus={filterSkus}
            filtersShown={filtersShown}
            reset={resetFiltersHandler}
          />
          <ProductsGrid
            categoryId={categoryId}
            categoryName={categoryName}
            subCategories={subCategories}
            skus={skusList}
            productsLoading={skusLoading}
            subCategoriesLoading={subCategoriesLoading}
            total={skusTotalCount}
            limit={limit}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            changePage={pageChangedHandler}
            filterSkus={filterSkus}
          />
        </div>
      </main>
    </div>
  );
};

export default ProductListing;
